import React, { useState, useEffect } from 'react';
import { 
  Upload, 
  FileText, 
  Trash2, 
  Eye,
  Filter,
  Search,
  Plus
} from 'lucide-react';
import { db } from '../firebase';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  addDoc,
  deleteDoc,
  doc, 
  orderBy 
} from 'firebase/firestore';
// Firebase Storage imports
import { storage } from '../firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

const SamplePaperUpload = () => {
  // States for boards, standards, subjects hierarchies
  const [boards, setBoards] = useState([]);
  const [standards, setStandards] = useState([]);
  const [subjects, setSubjects] = useState([]);
  
  // Selected hierarchy states
  const [selectedBoard, setSelectedBoard] = useState(null);
  const [selectedStd, setSelectedStd] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);
  
  // Sample paper states
  const [samplePapers, setSamplePapers] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterYear, setFilterYear] = useState('');
  
  // New sample paper form state
  const [newSamplePaper, setNewSamplePaper] = useState({
    title: '',
    description: '',
    year: new Date().getFullYear(),
    month: 'March',
    pdfFile: null,
    boardId: '',
    standardId: '',
    subjectId: '',
  });
  
  // Available months for dropdown
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June', 
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  
  // Fetch boards on component mount
  useEffect(() => {
    const fetchBoards = async () => {
      try {
        const boardsRef = collection(db, "boards");
        const snapshot = await getDocs(boardsRef);
        const boardsList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setBoards(boardsList);
      } catch (error) {
        console.error("Error fetching boards:", error);
      }
    };
    fetchBoards();
  }, []);

  // Fetch standards when board is selected
  useEffect(() => {
    const fetchStandards = async () => {
      if (!selectedBoard) return;
      
      try {
        const standardsRef = collection(db, "standards");
        const q = query(standardsRef, where("boardId", "==", selectedBoard));
        const snapshot = await getDocs(q);
        const standardsList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setStandards(standardsList);
      } catch (error) {
        console.error("Error fetching standards:", error);
      }
    };

    if (selectedBoard) {
      fetchStandards();
      setSelectedStd(null);
      setSelectedSubject(null);
      setNewSamplePaper(prev => ({...prev, boardId: selectedBoard}));
    }
  }, [selectedBoard]);

  // Fetch subjects when standard is selected
  useEffect(() => {
    const fetchSubjects = async () => {
      if (!selectedStd) return;
    
      try {
        const subjectsRef = collection(db, "subjects");
        const q = query(subjectsRef, where("standardId", "==", selectedStd));
        const snapshot = await getDocs(q);
        const subjectsList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setSubjects(subjectsList);
      } catch (error) {
        console.error("Error fetching subjects:", error);
      }
    };    

    if (selectedStd) {
      fetchSubjects();
      setSelectedSubject(null);
      setNewSamplePaper(prev => ({...prev, standardId: selectedStd}));
    }
  }, [selectedStd]);

  // Fetch sample papers when subject is selected
  useEffect(() => {
    const fetchSamplePapers = async () => {
      if (!selectedSubject) return;
    
      try {
        setLoading(true);
        const papersRef = collection(db, "samplePapers");
        const q = query(papersRef, where("subjectId", "==", selectedSubject));
        const snapshot = await getDocs(q);
        const papersList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setSamplePapers(papersList);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching sample papers:", error);
        setLoading(false);
      }
    };    

    if (selectedSubject) {
      fetchSamplePapers();
      setNewSamplePaper(prev => ({...prev, subjectId: selectedSubject}));
    }
  }, [selectedSubject]);

  // Handle file input change
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      setNewSamplePaper(prev => ({...prev, pdfFile: file}));
    } else {
      alert('Please select a valid PDF file');
    }
  };

  // Handle adding a new sample paper with Firebase Storage
  const handleAddSamplePaper = async () => {
    // Validation
    if (!newSamplePaper.title.trim()) {
      alert('Please enter a title for the sample paper');
      return;
    }
    
    if (!newSamplePaper.pdfFile) {
      alert('Please select a PDF file to upload');
      return;
    }
    
    if (!selectedBoard || !selectedStd || !selectedSubject) {
      alert('Please select board, standard, and subject');
      return;
    }
    
    try {
      setLoading(true);
      
      // Upload PDF to Firebase Storage
      const fileName = `sample-papers/${Date.now()}_${newSamplePaper.pdfFile.name}`;
      const storageRef = ref(storage, fileName);
      const snapshot = await uploadBytes(storageRef, newSamplePaper.pdfFile);
      const pdfUrl = await getDownloadURL(snapshot.ref);
      
      // Add document to Firestore
      const samplePapersRef = collection(db, "samplePapers");
      await addDoc(samplePapersRef, {
        title: newSamplePaper.title,
        description: newSamplePaper.description,
        year: newSamplePaper.year,
        month: newSamplePaper.month,
        pdfUrl: pdfUrl,
        pdfFileName: fileName, // Store the Firebase Storage path for deletion later
        boardId: selectedBoard,
        standardId: selectedStd,
        subjectId: selectedSubject,
        createdAt: new Date(),
      });
      
      // Refresh the list of sample papers
      const papersRef = collection(db, "samplePapers");
      const q = query(papersRef, where("subjectId", "==", selectedSubject));
      const querySnapshot = await getDocs(q);
      const papersList = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setSamplePapers(papersList);
      
      // Reset form and close modal
      setNewSamplePaper({
        title: '',
        description: '',
        year: new Date().getFullYear(),
        month: 'March',
        pdfFile: null,
        boardId: selectedBoard,
        standardId: selectedStd,
        subjectId: selectedSubject,
      });
      setShowAddModal(false);
      
    } catch (error) {
      console.error("Error adding sample paper:", error);
      alert('Error uploading sample paper. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle deleting a sample paper with Firebase Storage
  const handleDeleteSamplePaper = async (paperId, pdfFileName) => {
    try {
      if (!window.confirm('Are you sure you want to delete this sample paper?')) {
        return;
      }
      
      setLoading(true);
      
      // Delete PDF from Firebase Storage
      const fileRef = ref(storage, pdfFileName);
      await deleteObject(fileRef);
      
      // Delete document from Firestore
      await deleteDoc(doc(db, 'samplePapers', paperId));
      
      // Update state
      setSamplePapers(prevPapers => prevPapers.filter(paper => paper.id !== paperId));
      
    } catch (error) {
      console.error("Error deleting sample paper:", error);
      alert('Error deleting sample paper. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Filter sample papers based on search query and year
  const filteredPapers = samplePapers
    .filter(paper => 
      paper.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
      paper.description.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .filter(paper => 
      filterYear ? paper.year.toString() === filterYear : true
    );

  // Get unique years for filtering
  const years = [...new Set(samplePapers.map(paper => paper.year))].sort((a, b) => b - a);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-4 py-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">Sample Papers Management</h1>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
            disabled={!selectedSubject}
          >
            <Upload className="w-5 h-5" />
            Upload Sample Paper
          </button>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar Navigation */}
        <div className="w-64 bg-white h-[calc(100vh-80px)] border-r border-gray-200 p-4">
          {/* Boards Section */}
          <div className="mb-6">
            <h2 className="font-semibold text-gray-700 mb-2">Select Board</h2>
            <div className="space-y-2">
              {boards.map(board => (
                <button
                  key={board.id}
                  onClick={() => setSelectedBoard(board.id)}
                  className={`w-full text-left px-4 py-2 rounded-lg ${
                    selectedBoard === board.id ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50'
                  }`}
                >
                  {board.name}
                </button>
              ))}
            </div>
          </div>

          {/* Standards Section */}
          {selectedBoard && (
            <div className="mb-6">
              <h2 className="font-semibold text-gray-700 mb-2">Select Standard</h2>
              <div className="space-y-2">
                {standards.map(std => (
                  <button
                    key={std.id}
                    onClick={() => setSelectedStd(std.id)}
                    className={`w-full text-left px-4 py-2 rounded-lg ${
                      selectedStd === std.id ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50'
                    }`}
                  >
                    {std.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Subjects Section */}
          {selectedStd && (
            <div className="mb-6">
              <h2 className="font-semibold text-gray-700 mb-2">Select Subject</h2>
              <div className="space-y-2">
                {subjects.map(subject => (
                  <button
                    key={subject.id}
                    onClick={() => setSelectedSubject(subject.id)}
                    className={`w-full text-left px-4 py-2 rounded-lg ${
                      selectedSubject === subject.id ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50'
                    }`}
                  >
                    {subject.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Main Content Area */}
        <div className="flex-1 p-6">
          {selectedSubject ? (
            <>
              {/* Search and Filter Bar */}
              <div className="mb-6 flex flex-wrap gap-4">
                <div className="relative flex-1 min-w-[300px]">
                  <input
                    type="text"
                    placeholder="Search sample papers..."
                    className="w-full p-2 pl-10 border border-gray-300 rounded-lg"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                </div>
                
                <div className="relative flex items-center">
                  <select
                    className="p-2 pl-10 border border-gray-300 rounded-lg appearance-none pr-8"
                    value={filterYear}
                    onChange={(e) => setFilterYear(e.target.value)}
                  >
                    <option value="">All Years</option>
                    {years.map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                  <Filter className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                </div>
              </div>

              {loading ? (
                <div className="flex justify-center items-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                </div>
              ) : filteredPapers.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredPapers.map((paper) => (
                    <div 
                      key={paper.id} 
                      className="bg-white p-6 rounded-lg shadow border border-gray-200 hover:shadow-md transition"
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-gray-900 mb-1">{paper.title}</h3>
                          <p className="text-sm text-gray-500 mb-2">{paper.month} {paper.year}</p>
                          <p className="text-sm text-gray-600 mb-4">{paper.description}</p>
                        </div>
                        <div className="text-gray-400">
                          <FileText className="w-8 h-8" />
                        </div>
                      </div>
                      
                      <div className="flex gap-2 mt-auto">
                        <a
                          href={paper.pdfUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 py-1 px-3 bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 text-sm"
                        >
                          <Eye className="w-4 h-4" />
                          View
                        </a>
                        <button
                          onClick={() => handleDeleteSamplePaper(paper.id, paper.pdfFileName)}
                          className="flex items-center gap-1 py-1 px-3 bg-red-50 text-red-700 rounded-md hover:bg-red-100 text-sm"
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-white rounded-lg shadow border border-gray-200 p-12 text-center">
                  <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Sample Papers Found</h3>
                  <p className="text-gray-500 mb-6">Upload your first sample paper by clicking the button below.</p>
                  <button
                    onClick={() => setShowAddModal(true)}
                    className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                  >
                    <Upload className="w-5 h-5" />
                    Upload Sample Paper
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="bg-white rounded-lg shadow border border-gray-200 p-12 text-center">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Select a Subject</h3>
              <p className="text-gray-500">Choose a board, standard, and subject from the sidebar to view or upload sample papers.</p>
            </div>
          )}
        </div>
      </div>

      {/* Upload Sample Paper Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-lg">
            <div className="p-6">
              <h2 className="text-xl font-semibold mb-4">Upload Sample Paper</h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title
                  </label>
                  <input
                    type="text"
                    className="w-full p-2 border border-gray-300 rounded-lg"
                    placeholder="e.g., Physics Final Exam Sample"
                    value={newSamplePaper.title}
                    onChange={(e) => setNewSamplePaper(prev => ({...prev, title: e.target.value}))}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description (Optional)
                  </label>
                  <textarea
                    className="w-full p-2 border border-gray-300 rounded-lg"
                    rows={2}
                    placeholder="Brief description of the sample paper..."
                    value={newSamplePaper.description}
                    onChange={(e) => setNewSamplePaper(prev => ({...prev, description: e.target.value}))}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Year
                    </label>
                    <select
                      className="w-full p-2 border border-gray-300 rounded-lg"
                      value={newSamplePaper.year}
                      onChange={(e) => setNewSamplePaper(prev => ({...prev, year: parseInt(e.target.value)}))}
                    >
                      {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i).map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Month
                    </label>
                    <select
                      className="w-full p-2 border border-gray-300 rounded-lg"
                      value={newSamplePaper.month}
                      onChange={(e) => setNewSamplePaper(prev => ({...prev, month: e.target.value}))}
                    >
                      {months.map(month => (
                        <option key={month} value={month}>{month}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Upload PDF File
                  </label>
                  <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg">
                    <div className="space-y-1 text-center">
                      <Upload className="mx-auto h-12 w-12 text-gray-400" />
                      <div className="flex text-sm text-gray-600">
                        <label
                          htmlFor="file-upload"
                          className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500"
                        >
                          <span>Upload a file</span>
                          <input 
                            id="file-upload" 
                            name="file-upload" 
                            type="file"
                            accept="application/pdf"
                            className="sr-only" 
                            onChange={handleFileChange}
                          />
                        </label>
                        <p className="pl-1">or drag and drop</p>
                      </div>
                      <p className="text-xs text-gray-500">PDF up to 10MB</p>
                    </div>
                  </div>
                  {newSamplePaper.pdfFile && (
                    <p className="mt-2 text-sm text-gray-600">
                      Selected file: {newSamplePaper.pdfFile.name}
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-4">
                <button
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddSamplePaper}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  disabled={loading}
                >
                  {loading ? 'Uploading...' : 'Upload Sample Paper'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SamplePaperUpload;